export type LessonCultureNote = {
    title: string;
    progression: string;
    body: string;
    searchTerms: string;
};

/**
 * Original, short cultural context keyed to the lesson where a learner can
 * use it. Keep this separate from grammar prose so additional Genki-aligned
 * lessons can add one or more notes without changing the lesson renderer.
 */
export const lessonCultureNotes: Record<string, LessonCultureNote[]> = {
    "Lesson 5": [{
        title: "Calendars and public holidays",
        progression: "early calendar vocabulary",
        body: "Japanese calendars often mark national holidays in red. Plans may move around long holiday periods such as Golden Week, so checking the calendar matters as much as knowing the day name.",
        searchTerms: "calendar public holidays golden week days planning",
    }],
    "Lesson 6": [{
        title: "The school and work year",
        progression: "time and calendar basics",
        body: "The school and business year commonly begins in April, not January. That is why new-school and new-job events, cherry blossoms, and spring can appear together in everyday conversation.",
        searchTerms: "school year april work year cherry blossoms spring",
    }],
    "Lesson 8": [{
        title: "First meetings",
        progression: "self-introductions",
        body: "In a first meeting, a brief bow and a family name plus さん are safe defaults. よろしくお願いします closes an introduction warmly; it means something closer to “I look forward to working with you” than a word-for-word English phrase.",
        searchTerms: "first meeting bow family name san yoroshiku onegaishimasu introduction etiquette",
    }],
    "Lesson 9": [{
        title: "Counting politely",
        progression: "numbers and counters",
        body: "Counters are part of ordinary service language: staff may repeat a quantity back to confirm an order. Hearing a different counter is not a correction—it simply matches the kind of thing being counted.",
        searchTerms: "counters ordering shops service quantities confirmation",
    }],
    "Lesson 10": [{
        title: "Paying in shops",
        progression: "money and everyday transactions",
        body: "Cash is still common in Japan, although cards and mobile payments are widely used. At many counters, put cash or a card in the small tray rather than directly into someone’s hand, then wait for the change and receipt.",
        searchTerms: "money cash card payment shopping tray receipt etiquette",
    }],
    "Lesson 13": [{
        title: "Invitations and gentle refusals",
        progression: "making plans",
        body: "A direct “no” can sound abrupt in an invitation. ちょっと… followed by a pause or a brief reason often signals “that may be difficult”; listen to the situation, not only the literal words.",
        searchTerms: "invitation refusal chotto indirect plans social convention",
    }],
    "Lesson 15": [{
        title: "Entering a home",
        progression: "places and everyday life",
        body: "Shoes usually come off in the entry area, the 玄関 (げんかん), before stepping into a home. Slippers may be offered, but they are normally left outside rooms with tatami mats—and bathroom slippers stay in the bathroom.",
        searchTerms: "home shoes genkan slippers tatami bathroom etiquette",
    }],
    "Lesson 29": [{
        title: "Making requests",
        progression: "polite requests",
        body: "ください is useful for clear requests, while お願いします is common when asking a service worker for something. A calm tone and a short thank-you, ありがとうございます, do much of the politeness work.",
        searchTerms: "requests kudasai onegaishimasu service worker thank you politeness",
    }],
    "Lesson 30": [{
        title: "Permission in shared spaces",
        progression: "asking permission",
        body: "In shops, museums, classrooms, and trains, asking before taking photos or using a seat shows consideration for others. Posted rules are common; when unsure, a short いいですか is a helpful starting point.",
        searchTerms: "permission shared spaces photos museum classroom train rules etiquette",
    }],
    "Lesson 31": [{
        title: "Everyday public etiquette",
        progression: "rules and prohibitions",
        body: "Signs and announcements often use this lesson’s prohibition forms. On public transport, keeping calls quiet, lining up where marked, and leaving priority seating available are familiar ways to share a busy space.",
        searchTerms: "public transport etiquette signs announcements quiet calls queue priority seating",
    }],
    "Lesson 33": [{
        title: "Finding an address",
        progression: "directions and travel",
        body: "Japanese addresses move from a large area to smaller units, and a building name can be especially useful. In conversation, landmarks, station exits, and convenience stores are often easier navigation clues than a street name.",
        searchTerms: "directions addresses landmarks station exits convenience store navigation",
    }],
    "Lesson 36": [{
        title: "Going out together",
        progression: "activities and plans",
        body: "Plans are often made around train stations because they are natural meeting points. Confirming the station exit and a time is practical: a station can have several exits that lead to very different streets.",
        searchTerms: "activities plans station meeting point train exit time",
    }],
    "Lesson 38": [{
        title: "Talking about family",
        progression: "family vocabulary",
        body: "Japanese often uses one set of family words for your own family and respectful forms for another person’s. For example, 息子 (むすこ) and 娘 (むすめ) describe your own children, while 息子さん and 娘さん are considerate choices for someone else’s.",
        searchTerms: "family vocabulary own family another persons family son daughter musuko musume honorific san",
    }],
    "Lesson 40": [{
        title: "Softening an opinion",
        progression: "sharing opinions",
        body: "と思います lets a speaker offer a view without presenting it as the only answer. This kind of softening is useful in meetings, class, and everyday conversation, especially when you do not know the other person well.",
        searchTerms: "opinion soften meetings class social convention to omoimasu",
    }],
    "Lesson 43": [{
        title: "Giving reminders considerately",
        progression: "negative requests",
        body: "ないでください can be firm, so context and tone matter. Staff notices may use it directly for safety, while between acquaintances a reason or softer phrase can make a reminder feel less personal.",
        searchTerms: "negative requests reminders safety notices tone acquaintances",
    }],
    "Lesson 51": [{
        title: "Choosing a speech style",
        progression: "plain forms and casual conversation",
        body: "Plain forms are normal with close friends and family, but politeness is more than grammar. When you are unsure of the relationship, beginning with です／ます and adjusting after listening is a dependable choice.",
        searchTerms: "plain form casual speech polite desu masu friends family relationship",
    }],
    "Lesson 55": [{
        title: "Making a choice without pressure",
        progression: "comparisons and preferences",
        body: "When choosing food, gifts, or an outing for a group, people may phrase a preference as a suggestion rather than a demand. Asking what others prefer and offering options helps everyone take part in the decision.",
        searchTerms: "comparisons preferences group choice food gifts outing suggestion",
    }],
    "Lesson 60": [{
        title: "Saying what you want",
        progression: "personal wants and plans",
        body: "〜たい describes your own wish, not a command or a request to another person. In a restaurant or shop, pair a polite request with the item instead; this keeps a personal desire distinct from asking someone to act.",
        searchTerms: "want tai restaurant shop polite request personal desire",
    }],
    "Lesson 62": [{
        title: "Sharing experiences",
        progression: "travel and life experiences",
        body: "Questions about places you have visited, foods you have tried, or festivals you have attended are easy conversation openers. A simple follow-up such as どうでしたか invites a story without assuming the experience was good or bad.",
        searchTerms: "experience travel food festivals conversation opener dou deshita ka",
    }],
    "Lesson 66": [{
        title: "Rules, obligations, and context",
        progression: "obligations",
        body: "Obligation expressions are common in school, work, and travel instructions. They can describe a real rule, but speakers also soften personal advice by explaining why something is needed rather than simply telling someone what to do.",
        searchTerms: "obligation rules school work instructions advice context",
    }],
    "Lesson 68": [{
        title: "Gentle questions in service settings",
        progression: "polite questions",
        body: "でしょうか is useful with strangers, staff, and new acquaintances because it leaves room for the other person to respond. It is one small way Japanese can make a question sound less demanding without making it vague.",
        searchTerms: "polite questions strangers staff service setting gentle deshou ka",
    }],
};
